import React from "react";
import { fetchBookingV4FromAPI } from "../../../../../APIs/Booking/GetBooking";
import { mapBookingV4ToState } from "../../../../../APIs/Booking/UpdateBooking";
import { DiaryContextType } from "../../../../../context/DiaryContext";
import { BookingV4, Deposit } from "../../../../../types/bookings";
import { getBookingRefHyperlink } from "../SelectedBookingLinked/LinkedBookingCustomFields";
import { SelectedBookingTabEnum } from "../Tabs/SelectedBookingTabsController";
import { SelectedBookingPaymentNote } from "./SelectedBookingPaymentNote";
import { RefundClaimNote } from "./RefundClaimNote";
import {
  addDeposit,
  claimDeposit,
  refundDeposit
} from "../../../../../APIs/Booking/PaymentOperations";
import { PaymentFormData } from "./AddPaymentModal";
import { RefundFormData } from "./RefundModal";
import {
  formatCurrency,
  parseDepositAmount
} from "../../../../../utils/number";
import { getPaymentTypeDisplayName } from "../../../../../types/paymentTypes";
import { EDSBadge } from "../../../../eds/EDSBadge";
import { TableColumns } from "../../../../Shared/Table/Table";

export type GetPaymentsDataReturnType = {
  reference?: JSX.Element | null;
  displayDate: string | JSX.Element;
  displayAmount: string;
  displayType: string | JSX.Element;
  displayPaidBy: string;
  displayCard: string;
  displayStatus: string | JSX.Element;
  displayStatusIcon: JSX.Element;
  displayNote: JSX.Element | null;
  deleteButton?: JSX.Element | null;
  actions?: JSX.Element | null;
  id: string;
  type: string;
  status: string;
  amount: number | { amount: number };
  claims?: Array<{ amount: number | { amount: number } }>;
  refunds?: string[] | null;
  refund_ids?: string[] | null;
  dispute?: boolean;
  notes?: string;
  rowType: "parent" | "claim" | "refund";
};
export const getPaymentsData = (
  selectedBookingV4?: BookingV4
): GetPaymentsDataReturnType[] => {
  if (!selectedBookingV4?.deposits) {
    return [];
  }

  const allRows: GetPaymentsDataReturnType[] = [];

  selectedBookingV4.deposits.forEach((deposit) => {
    const initials = getInitials(deposit.user?.name ?? "");
    let initialsDisplay = "";
    if (initials !== "") {
      initialsDisplay = `(${initials})`;
    }

    const payment = getPaymentByRef(selectedBookingV4, deposit.payment_ref);

    const paidType = deposit?.paid_type;
    let typeDisplay = getPaymentTypeDisplayName(deposit.type);
    if (paidType) {
      typeDisplay = paidType;
    }

    const cardSuffix = payment?.card_suffix;
    const displayCard = cardSuffix ? `**${cardSuffix}` : "";

    const cardholderName = payment?.cardholder_name;
    const displayPaidBy = cardholderName ?? "";

    const displayStatus = getPaymentStatus(selectedBookingV4, deposit);
    const statusBadge = (
      <EDSBadge variant={deposit.status === "paid" ? "success" : "warning"}>
        {displayStatus}
      </EDSBadge>
    );

    const parentRow: GetPaymentsDataReturnType = {
      ...deposit,
      displayDate: `${new Date(deposit.date).toLocaleString(undefined, {
        dateStyle: "medium"
      })} ${initialsDisplay}`,
      displayType: typeDisplay,
      displayPaidBy,
      displayCard,
      displayAmount: formatCurrency(
        parseDepositAmount(deposit.amount),
        deposit.currency
      ),
      displayStatus: statusBadge,
      displayStatusIcon: statusBadge,
      displayNote: SelectedBookingPaymentNote({ deposit }),
      id: deposit.id,
      rowType: "parent"
    };

    allRows.push(parentRow);

    if (deposit.claims && deposit.claims.length > 0) {
      const sortedClaims = [...deposit.claims].sort(
        (a, b) =>
          new Date(a.created_date).getTime() -
          new Date(b.created_date).getTime()
      );

      sortedClaims.forEach((claim) => {
        const dateStr = new Date(claim.created_date).toLocaleString(undefined, {
          dateStyle: "medium"
        });

        const claimRow: GetPaymentsDataReturnType = {
          id: `${deposit.id}-claim-${claim.payment_ref || Math.random()}`,
          displayDate: React.createElement(
            "span",
            { className: "BookingPayments-NestedRow" },
            dateStr
          ),
          displayType: React.createElement(
            "span",
            {
              className: "BookingPayments-ClaimRow",
              style: { fontWeight: 500 }
            },
            "Claim"
          ),
          displayAmount: formatCurrency(
            parseDepositAmount(claim.amount),
            deposit.currency
          ),
          displayStatus: <EDSBadge variant="success">Claimed</EDSBadge>,
          displayStatusIcon: <EDSBadge variant="success">Claimed</EDSBadge>,
          displayPaidBy: "",
          displayCard: "",
          displayNote: null,
          type: "claim",
          status: "claimed",
          amount: claim.amount,
          rowType: "claim"
        };

        allRows.push(claimRow);
      });
    }

    const refundRefs = deposit.refunds || deposit.refund_ids || [];
    if (refundRefs && refundRefs.length > 0) {
      const refundDetails = refundRefs
        .map((refundRef) => getRefundByRef(selectedBookingV4, refundRef))
        .filter((refund) => refund !== undefined);

      const sortedRefunds = [...refundDetails].sort(
        (a, b) =>
          new Date(a.created_date).getTime() -
          new Date(b.created_date).getTime()
      );

      sortedRefunds.forEach((refund) => {
        const dateStr = new Date(refund.created_date).toLocaleString(
          undefined,
          {
            dateStyle: "medium"
          }
        );

        const refundRow: GetPaymentsDataReturnType = {
          id: `${deposit.id}-refund-${refund.refund_ref || refund.id}`,
          displayDate: React.createElement(
            "span",
            { className: "BookingPayments-NestedRow" },
            dateStr
          ),
          displayType: React.createElement(
            "span",
            {
              className: "BookingPayments-RefundRow",
              style: { fontWeight: 500 }
            },
            "Refund"
          ),
          displayAmount: formatCurrency(
            parseDepositAmount(refund.amount),
            deposit.currency
          ),
          displayStatus: <EDSBadge variant="destructive">Refunded</EDSBadge>,
          displayStatusIcon: (
            <EDSBadge variant="destructive">Refunded</EDSBadge>
          ),
          displayPaidBy: "",
          displayCard: "",
          displayNote: refund.notes
            ? RefundClaimNote({ notes: refund.notes })
            : null,
          type: "refund",
          status: "refunded",
          amount: refund.amount,
          rowType: "refund"
        };

        allRows.push(refundRow);
      });
    }
  });

  return allRows;
};

const getPaymentByRef = (selectedBookingV4: BookingV4, paymentRef: string) =>
  selectedBookingV4.payments?.filter(
    (row) => row.payment_ref === paymentRef
  )?.[0];

const getRefundByRef = (selectedBookingV4: BookingV4, refundRef: string): any =>
  selectedBookingV4.refunds?.find(
    (refund) => refund.refund_ref === refundRef || refund.id === refundRef
  );

const getInitials = (fullName: string): string => {
  const nameParts = fullName.split(" ");
  let initials = "";

  for (const i in nameParts) {
    initials += nameParts[i].slice(0, 1);
  }

  return initials.toUpperCase();
};

const getPaymentStatus = (selectedBookingV4: BookingV4, deposit: Deposit) => {
  let status = "";
  if (deposit.status !== "paid") {
    if (deposit.type === "request_auth" || deposit.type === "request_payment") {
      status = "Requested";
    }
    if (deposit.type === "manual_auth" || deposit.type === "manual_payment") {
      status = "In progress";
    }
  }
  if (deposit.status === "paid") {
    if (
      [
        "card_authorised",
        "request_auth",
        "manual_auth",
        "manual_payment"
      ].indexOf(deposit.type) > -1
    ) {
      status = "Completed";
    }
    if (!deposit.type || (deposit.type as string) === "paid") {
      status = "Paid";
    }
    if (
      deposit.type === "request_payment" ||
      deposit.type === "customer_preorder"
    ) {
      status = "Received";
    }
  }

  const payment = getPaymentByRef(selectedBookingV4, deposit.payment_ref);

  if (
    (deposit.type == "manual_payment" || deposit.type == "request_payment") &&
    payment
  ) {
    status += ` on ${new Date(payment.created_date).toLocaleString("en-GB", {
      day: "numeric",
      month: "short"
    })}`;
  }
  return status;
};

export const getBookingPaidTotal = (selectedBookingV4?: BookingV4): number => {
  let total = 0;
  selectedBookingV4?.deposits?.forEach((deposit) => {
    // Treat deposits without a type as non-Collins payments
    if (!deposit.type || deposit.status === "paid") {
      // Only include the claimed amount for auth deposits
      if (deposit.type && deposit.type.indexOf("_auth") > 0) {
        deposit.claims?.forEach((claim) => {
          total += parseDepositAmount(claim.amount);
        });
      } else {
        total += parseDepositAmount(deposit.amount);
      }
    }

    const refunds = deposit.refunds || deposit.refund_ids;
    refunds?.forEach((refundRef) => {
      // Refund ID key depends whether data from Collins API or Core.
      const refund = selectedBookingV4.refunds?.find(
        (bookingRefund) =>
          bookingRefund.id === refundRef ||
          bookingRefund.refund_ref === refundRef
      );
      if (refund) {
        total -= parseDepositAmount(refund.amount);
      }
    });
  });
  return total;
};

type GetLinkedBookingDetailsType = {
  linkedBookingIds?: string[];
  selectedBookingV4?: BookingV4;
};
export const getLinkedBookingDetails = async ({
  linkedBookingIds,
  selectedBookingV4
}: GetLinkedBookingDetailsType): Promise<(BookingV4 | undefined)[]> => {
  if (!linkedBookingIds) {
    return [];
  }

  return await Promise.all(
    linkedBookingIds.map(async (id) => {
      if (id === selectedBookingV4?._id) {
        // payment on current booking
        return selectedBookingV4;
      }
      // load booking
      return await fetchBookingV4FromAPI(id);
    })
  );
};

type getPaymentsDataAllReturn = {
  bookingPayments: GetPaymentsDataReturnType[];
  amountTotal: number;
};
type getPaymentsDataAllProps = {
  linkedBookingsData?: (BookingV4 | undefined)[];
  selectedBookingV4?: BookingV4;
  isLoading: boolean;
  diaryContext: DiaryContextType;
  linkedBookingIds?: string[];
  setSelectedTabKey?: (value: SelectedBookingTabEnum) => void;
};
export const getPaymentsDataAll = ({
  linkedBookingsData,
  isLoading,
  diaryContext,
  linkedBookingIds,
  setSelectedTabKey
}: getPaymentsDataAllProps): getPaymentsDataAllReturn => {
  const bookingPayments: GetPaymentsDataReturnType[] = [];
  const amountTotal = 0;
  if (isLoading || !diaryContext.selectedBookingV4) {
    // still loading
    return { bookingPayments, amountTotal };
  }

  if (linkedBookingsData && linkedBookingsData.length > 1) {
    // Linked booking tab - return all linked booking payments
    return {
      bookingPayments: linkedBookingsData.reduce((prevData, booking) => {
        if (!booking) {
          return prevData;
        }
        // Add custom JSX - reference hyperlink column
        const reference = getBookingRefHyperlink(
          {
            ...mapBookingV4ToState(booking),
            linkedBookings: diaryContext?.selectedBooking?.linkedBookings
          },
          diaryContext,
          setSelectedTabKey,
          SelectedBookingTabEnum.Payments
        );
        return prevData.concat(
          getPaymentsData(booking).map((row) => ({
            ...row,
            reference
          }))
        );
      }, bookingPayments),
      amountTotal: linkedBookingsData.reduce((total, booking) => {
        return (total += getBookingPaidTotal(booking));
      }, amountTotal)
    };
  }

  if (linkedBookingIds) {
    // Linked booking tab - no linked bookings/payments found
    return { bookingPayments, amountTotal };
  }

  return {
    // Payments tab - return current booking payments
    bookingPayments: getPaymentsData(diaryContext.selectedBookingV4),
    amountTotal: getBookingPaidTotal(diaryContext.selectedBookingV4)
  };
};

export const handleAddPayment = async (
  booking: BookingV4,
  paymentData: PaymentFormData
): Promise<void> => {
  try {
    await addDeposit(booking, paymentData);
  } catch (error) {
    console.error("Error adding payment:", error);
    throw error;
  }
};

export const handleClaimPayment = async (
  booking: BookingV4,
  depositId: string,
  amount?: number
): Promise<BookingV4 | undefined> => {
  try {
    const result = await claimDeposit(booking, depositId, amount);
    if (result?.booking) {
      return result.booking;
    }
    const updatedBooking = await fetchBookingV4FromAPI(booking._id);
    return updatedBooking;
  } catch (error) {
    console.error("Error claiming payment:", error);
    throw error;
  }
};

export type PaymentTableRow = {
  reference?: React.JSX.Element | null;
  displayType: string | React.JSX.Element;
  displayAmount: string;
  displayDate: string | React.JSX.Element;
  displayStatus: string | React.JSX.Element;
  displayNote: React.JSX.Element | null;
  actions: React.JSX.Element | null;
};

export const getPaymentTableColumns = (
  linkedBookingIds?: string[]
): TableColumns<PaymentTableRow>[] => [
  ...(linkedBookingIds
    ? [
        {
          caption: "DMN Ref #",
          dataNameJSX: "reference" as const
        }
      ]
    : []),
  { caption: "Type", dataNameJSX: "displayType" as const },
  { caption: "Amount", dataName: "displayAmount" as const },
  { caption: "Date", dataNameJSX: "displayDate" as const },
  { caption: "Status", dataNameJSX: "displayStatus" as const },
  { caption: "Notes", dataNameJSX: "displayNote" as const },
  { caption: "", dataNameJSX: "actions" as const }
];

export const buildPaymentTableData = (
  bookingPayments: GetPaymentsDataReturnType[],
  buildActions: (payment: GetPaymentsDataReturnType) => React.JSX.Element | null
): PaymentTableRow[] =>
  bookingPayments.map((payment) => ({
    reference: payment.reference,
    displayType: payment.displayType,
    displayAmount: payment.displayAmount,
    displayDate: payment.displayDate,
    displayStatus: payment.displayStatus,
    displayNote: payment.displayNote,
    actions: buildActions(payment)
  }));

export const handleRefundPayment = async (
  booking: BookingV4,
  depositId: string,
  refundData: RefundFormData
): Promise<BookingV4 | undefined> => {
  try {
    const result = await refundDeposit(
      booking,
      depositId,
      refundData.amount,
      refundData.notes
    );
    if (result?.booking) {
      return result.booking;
    }
    const updatedBooking = await fetchBookingV4FromAPI(booking._id);
    return updatedBooking;
  } catch (error) {
    console.error("Error processing refund:", error);
    throw error;
  }
};
